import { Normalization } from '~/utils/normalization'
import { CurrencyVerdict } from '~/enums/CurrencyVerdict'
import type { CurrencyEntry } from '~/types/CurrencyEntry'
import type { ApiFieldIssue } from '~/services/api/ApiFieldIssue'

// Doubles hold 15 significant digits exactly, so integer digits plus minor-unit digits stay within 15.
const MAX_SIGNIFICANT_DIGITS = 15

type Verdict = (typeof CurrencyVerdict)[keyof typeof CurrencyVerdict]

/** Builds the currency utility over a registry of supported ISO 4217 codes; the first code is the default. */
export function createCurrencyAmount(allowed: readonly string[]) {
  const [defaultCurrency] = allowed
  if (defaultCurrency === undefined) throw new RangeError('A currency registry needs a currency')

  const supported = (currency: string): string => {
    if (!allowed.includes(currency)) throw new RangeError(`Unsupported currency ${currency}`)
    return currency
  }

  const currencyFormat = (currency: string, locale: string) =>
    new Intl.NumberFormat(locale, { currency: supported(currency), style: 'currency' })

  const minorUnits = (currency: string): number =>
    currencyFormat(currency, 'en-US').resolvedOptions().maximumFractionDigits ?? 2

  const maxIntegerDigits = (currency: string): number =>
    MAX_SIGNIFICANT_DIGITS - minorUnits(currency)

  const partOf = (currency: string, locale: string, type: Intl.NumberFormatPartTypes): string =>
    currencyFormat(currency, locale)
      .formatToParts(1234567.5)
      .find((part) => part.type === type)?.value ?? ''

  const separators = (currency: string, locale: string) => ({
    decimal: partOf(currency, locale, 'decimal') || '.',
    group: partOf(currency, locale, 'group') || ','
  })

  const symbol = (currency: string, locale: string): string => partOf(currency, locale, 'currency')

  const groupedInteger = (whole: string, locale: string): string =>
    new Intl.NumberFormat(locale, { useGrouping: true }).format(BigInt(whole))

  /** Reduces any entry to the editable text (grouped, no symbol) and amount; rejected characters and surplus digits are dropped. */
  function sanitize(raw: string, currency: string, locale: string): CurrencyEntry {
    const { decimal, group } = separators(currency, locale)
    const minor = minorUnits(currency)
    const canonical = raw
      .split(group)
      .join('')
      .split(decimal)
      .join('.')
      .replace(/[^\d.]/gu, '')
    const [integerPart = '', ...fractionParts] = canonical.split('.')
    const hasDecimal = fractionParts.length > 0 && minor > 0
    const fraction = fractionParts.join('').slice(0, minor)
    const integer = integerPart.replace(/^0+(?=\d)/u, '').slice(0, maxIntegerDigits(currency))

    if (!integer && fractionParts.length === 0) return { hasAmount: false, text: '' }

    const whole = integer || '0'
    return {
      hasAmount: true,
      text: `${groupedInteger(whole, locale)}${hasDecimal ? `${decimal}${fraction}` : ''}`,
      value: Normalization.floatValue(fraction ? `${whole}.${fraction}` : whole, 0) ?? 0
    }
  }

  const isBlank = (value: number | string | null | undefined): value is null | undefined | '' =>
    value === null || value === undefined || (typeof value === 'string' && value.trim() === '')

  const amountOf = (value: number | string | null | undefined): number | null => {
    if (isBlank(value)) return null
    const amount = Normalization.floatValue(value, null)
    return amount !== null && Number.isFinite(amount) ? amount : null
  }

  /** The currency text a field shows when not focused, e.g. `$1,234.50`; empty for no amount. */
  function format(
    value: number | string | null | undefined,
    currency: string,
    locale: string
  ): string {
    const amount = amountOf(value)
    if (amount === null) return ''
    const minor = minorUnits(currency)
    return new Intl.NumberFormat(locale, {
      currency: supported(currency),
      maximumFractionDigits: minor,
      minimumFractionDigits: minor,
      style: 'currency'
    }).format(amount)
  }

  /** The text a focused field shows for a model value: grouped digits without the symbol. */
  function edit(
    value: number | string | null | undefined,
    currency: string,
    locale: string
  ): string {
    const amount = amountOf(value)
    if (amount === null) return ''
    const plain = String(amount)
    const canonical = plain.includes('e') ? amount.toFixed(minorUnits(currency)) : plain
    const text = canonical.replace('.', separators(currency, locale).decimal)
    return sanitize(text, currency, locale).text
  }

  /** How many digits and decimal separators an entry holds once sanitized; caret positions map through this count. */
  function significantCount(raw: string, currency: string, locale: string): number {
    const { decimal } = separators(currency, locale)
    return sanitize(raw, currency, locale)
      .text.split('')
      .filter((char) => /\d/u.test(char) || char === decimal).length
  }

  /** The caret position in `text` after `count` significant characters. */
  function caretAfter(text: string, count: number, currency: string, locale: string): number {
    if (count === 0) return 0
    const { decimal } = separators(currency, locale)
    let seen = 0
    for (let index = 0; index < text.length; index += 1) {
      const char = text.charAt(index)
      if (/\d/u.test(char) || char === decimal) seen += 1
      if (seen === count) return index + 1
    }
    return text.length
  }

  /** Validates a raw entry against the currency's separators and minor units. */
  function verdict(raw: string | null | undefined, currency: string, locale: string): Verdict {
    const text = (raw ?? '').replace(/\s/gu, '')
    if (text === '') return CurrencyVerdict.EMPTY
    const { decimal, group } = separators(currency, locale)
    const bare = text.split(symbol(currency, locale).replace(/\s/gu, '')).join('')
    const unmarked = bare.split(group).join('').split(decimal).join('.')
    if (/[^\d.]/u.test(unmarked)) return CurrencyVerdict.INVALID_CHARACTERS
    const [integer = '', fraction = '', ...rest] = unmarked.split('.')
    const hasDigits = integer.length + fraction.length > 0
    const withinLimits =
      rest.length === 0 &&
      integer.length <= maxIntegerDigits(currency) &&
      fraction.length <= minorUnits(currency)
    return hasDigits && withinLimits ? CurrencyVerdict.VALID : CurrencyVerdict.INVALID_AMOUNT
  }

  /** The typed field-error code and params that present a failing verdict through the field-error presenter. */
  function fieldIssue(failing: Verdict): Pick<ApiFieldIssue, 'code' | 'params'> {
    if (failing === CurrencyVerdict.EMPTY) return { code: 'FIELD_REQUIRED' }
    if (failing === CurrencyVerdict.VALID)
      throw new TypeError('A valid currency entry has no field issue')
    return { code: 'FIELD_INVALID_FORMAT', params: { expected_format: 'decimal' } }
  }

  return {
    caretAfter,
    defaultCurrency,
    edit,
    fieldIssue,
    format,
    minorUnits,
    sanitize,
    separators,
    significantCount,
    supported,
    symbol,
    verdict
  }
}
